
import { provideApolloClient } from "@vue/apollo-composable";
import { ApolloClient, InMemoryCache, createHttpLink, split } from "@apollo/client/core";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import eventbus from "./eventbus";

// Name of the localStorage item
const AUTH_TOKEN = "apollo-token";

if (location.hash) {
  const matches = location.hash.match(/api_token=(.*?)([&?]|$)/);
  if (matches && matches.length > 1) {
    localStorage.setItem(AUTH_TOKEN, matches[1]);
  }
  location.hash = "";
}

// Http endpoint
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP || "http://localhost:4000/";

// WebSocket endpoint
const wsEndpoint = process.env.VUE_APP_GRAPHQL_WS || "ws://localhost:4000/";

// Create an http link
const httpLink = createHttpLink({
  uri: httpEndpoint,
});

// Create a WebSocket link
const wsLink = new WebSocketLink({
  uri: wsEndpoint,
  options: {
    reconnect: true,
    connectionParams: () => {
      return {
          Authorization: localStorage.getItem(AUTH_TOKEN) || "",
      }
    }
  },
});

// Using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent.
const link = split(
  // Split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

// Set up authentication link
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const gerror of graphQLErrors) {
      if (gerror.extensions && gerror.extensions.code === "UNAUTHENTICATED") {
        console.error(
          "%cUnauthenticated",
          "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
          gerror.message
        );
        onLogout().then(() => {
          setTimeout(() => location.reload(), 1000);
        });
        return;
      }
    }
  }
  if (networkError) {
    console.error(
      "%cNetwork Error",
      "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
      networkError.message
    );
  }
});

// Create the Apollo Client
const apolloClient = new ApolloClient({
  link: authLink.concat(errorLink).concat(link),
  cache: new InMemoryCache(),
});
/* For better cache handling, implemented in old apollo client, we should find a way to do the same here
cache: new InMemoryCache({
        dataIdFromObject: (object: any) => {
            if (object && object.__typename && object.uuid) return object.__typename + ":" + object.uuid
            return null
        },
    }),*/

export function provideClient() {
  provideApolloClient(apolloClient);
  return apolloClient;
}

// Manually call this when user log in
export async function onLogin(token: string) {
  if (typeof localStorage !== "undefined" && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  //@ts-ignore
  wsLink.subscriptionClient.close(false, false)
  //@ts-ignore
  wsLink.subscriptionClient.connect()
  try {
    await apolloClient.resetStore();
  } catch (e: any) {
    console.error("%cError on cache reset (login)", "color: orange;", e.message);
  }
}

// Manually call this when user log out
export async function onLogout() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_TOKEN);
  }
  try {
    await apolloClient.resetStore();
  } catch (e: any) {
    console.error("%cError on cache reset (logout)", "color: orange;", e.message);
  }
}

export function isAuthenticated(): boolean {
  return typeof localStorage !== "undefined" && !!localStorage.getItem(AUTH_TOKEN);
}